import { ParamsForMethod, TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValue } from "@tellescope/types-models";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { readUtmParams } from "../../utils";
import type { FormFieldNode } from "../../utils/types";
import type { FormShallowStoreHook } from "../../store";
import type { useTellescopeFormCore } from "../use-tellescope-form-core/use-tellescope-form-core";
import { buildResponsesToSubmit } from "./build-responses-to-submit";
import { includedFieldIdsOf, type FieldStack } from "./field-stack";
import { useTrackFormSubmitted } from "../use-form-analytics";

export interface UseEnduserSubmitFormArgs {
  core: ReturnType<typeof useTellescopeFormCore>;
  activeField: FormFieldNode | undefined;
  fieldStack: FieldStack;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  existingResponses: FormResponseValue[] | undefined;
  response: { accessCode?: string } | undefined;
  formId: string;
  enduserId: string;
  parentResponseId: string | undefined;
  rootResponseId: string | undefined;
  automationStepId: string | undefined;
  useFormShallowStore: FormShallowStoreHook;
}

export const useEnduserSubmitForm = ({
  core,
  activeField,
  fieldStack,
  fieldsMap,
  existingResponses,
  response,
  formId,
  enduserId,
  parentResponseId,
  rootResponseId,
  automationStepId,
  useFormShallowStore,
}: UseEnduserSubmitFormArgs) => {
  const { t } = useTranslation("forms");
  const { setServerRedirectUrl, setSubmittedResponse } = useFormShallowStore(
    "setServerRedirectUrl",
    "setSubmittedResponse"
  );
  const trackFormSubmitted = useTrackFormSubmitted();

  return useCallback(async () => {
    const currentError = core.validateField({ fieldId: activeField?.value.id ?? "" });
    if (currentError) {
      throw new Error(t("Please fix validation errors before submitting"));
    }

    return core.runSubmit(async (uploadFirst) => {
      let formResponse = response ?? core.preparedResponse;

      if (!formResponse) {
        const res = await core.prepareFormResponseMutation.mutateAsync({
          formId,
          enduserId,
          parentResponseId,
          rootResponseId,
          automationStepId,
          title: core.formTitle,
        });
        formResponse = res.response;
      }

      if (!formResponse || !formResponse.accessCode) {
        throw new Error(t("Failed to prepare form response"));
      }

      const submittedFieldIds = includedFieldIdsOf(fieldStack, core.isSubFieldHidden);

      const responsesAfterUpload = await uploadFirst();

      const responsesToSubmit = buildResponsesToSubmit({
        responsesAfterUpload,
        existingResponses,
        submittedFieldIds,
        enduser: core.enduser,
        fieldsMap,
      });

      const productIds = responsesToSubmit.flatMap((r) => core.fields?.fieldsMap[r.fieldId]?.options?.productIds ?? []);
      const payload: ParamsForMethod<"form_responses", "submit_form_response"> = {
        accessCode: formResponse.accessCode,
        responses: responsesToSubmit,
        utm: readUtmParams(),
      };
      if (automationStepId) payload.automationStepId = automationStepId;
      if (core.customerId) payload.customerId = core.customerId;
      if (productIds.length) payload.productIds = productIds;

      const result = await core.submitFormMutation.mutateAsync(payload);

      // Recorded rather than returned so the autosubmit path, which discards the result, still
      // reaches the destination the server chose. `nextFormGroupPublicURL` is deliberately not
      // followed: it is a path on the provider webapp's public `/e/form` page, which legacy chained
      // through only from that page, never from the portal.
      setServerRedirectUrl(result.redirectTo);
      setSubmittedResponse(result.formResponse);
      // After the mutation resolves, so a failed submit is never reported as a conversion. Covers
      // the autosubmit path too, which reaches its thanks screen without going through the button.
      trackFormSubmitted(formId);

      return result;
    });
  }, [
    core,
    response,
    activeField?.value.id,
    formId,
    enduserId,
    parentResponseId,
    rootResponseId,
    automationStepId,
    fieldStack,
    existingResponses,
    fieldsMap,
    setServerRedirectUrl,
    setSubmittedResponse,
    trackFormSubmitted,
    t,
  ]);
};
